'use client';

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Camera, Check, Info, Loader2, X } from 'lucide-react';
import { SUPPORT_LIMITS } from '@/lib/schemas/support.schema';
import { captureTab, toWebp } from '@/lib/support/capture';

/** Une capture du brouillon : l'image deja reduite, et son adresse locale pour la miniature */
export interface DraftShot {
  id: string;
  blob: Blob;
  url: string;
}

let nextId = 0;
export const draftShot = (blob: Blob): DraftShot => ({ id: `shot-${++nextId}`, blob, url: URL.createObjectURL(blob) });
export const releaseShots = (shots: DraftShot[]) => shots.forEach(s => URL.revokeObjectURL(s.url));

/** Ajoute les captures au formulaire (les envoie dans le champ « shots » du multipart) */
export const appendShots = (form: FormData, shots: DraftShot[]) =>
  shots.forEach((s, i) => form.append('shots', s.blob, `capture-${i + 1}.${s.blob.type === 'image/png' ? 'png' : 'webp'}`));

type Notice = 'added' | 'cancelled' | 'fallback' | 'failed' | 'unsupported' | null;

const ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * Les captures d'un message (P03) : capturer l'onglet, coller une image (Ctrl+V dans la zone qui porte
 * data-shots-scope), joindre un fichier. Trois au plus (R3). Pendant la capture, onHide masque le panneau.
 */
export function ShotsField({ shots, onChange, onHide }: {
  shots: DraftShot[];
  onChange: (shots: DraftShot[]) => void;
  onHide?: (hidden: boolean) => void;
}) {
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // La liste a jour, pour les ajouts qui finissent apres un autre changement
  const shotsRef = useRef(shots);
  const onChangeRef = useRef(onChange);
  useEffect(() => { shotsRef.current = shots; onChangeRef.current = onChange; });
  const full = shots.length >= SUPPORT_LIMITS.shotsMax;

  const add = (blob: Blob) => {
    if (shotsRef.current.length >= SUPPORT_LIMITS.shotsMax) return;
    const next = [...shotsRef.current, draftShot(blob)];
    shotsRef.current = next;
    onChangeRef.current(next);
  };

  const addFiles = async (files: Iterable<File>) => {
    for (const file of files) {
      if (!ACCEPT.split(',').includes(file.type)) { setNotice('unsupported'); continue; }
      // Au-dela de trois, la phrase des trois captures s'affiche deja
      if (shotsRef.current.length >= SUPPORT_LIMITS.shotsMax) return;
      try { add(await toWebp(file)); setNotice(null); } catch { setNotice('failed'); }
    }
  };
  const addFilesRef = useRef(addFiles);
  useEffect(() => { addFilesRef.current = addFiles; });

  const capture = async () => {
    setPending(true);
    setNotice(null);
    onHide?.(true);
    try {
      // Deux images, pour que le panneau ait disparu de l'ecran avant la prise
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      const result = await captureTab();
      if (result === 'cancelled') setNotice('cancelled');
      else { add(result.blob); setNotice(result.fallback ? 'fallback' : 'added'); }
    } catch {
      setNotice('failed');
    } finally {
      onHide?.(false);
      setPending(false);
    }
  };

  // Coller une image (Ctrl+V) n'importe ou dans la zone du formulaire
  useEffect(() => {
    const scope = ref.current?.closest('[data-shots-scope]') ?? ref.current;
    if (!scope) return;
    const onPaste = (e: Event) => {
      const files = [...((e as ClipboardEvent).clipboardData?.files ?? [])].filter(f => f.type.startsWith('image/'));
      if (!files.length) return;
      e.preventDefault();
      void addFilesRef.current(files);
    };
    scope.addEventListener('paste', onPaste);
    return () => scope.removeEventListener('paste', onPaste);
  }, []);

  const remove = (id: string) => {
    const gone = shots.find(s => s.id === id);
    if (gone) releaseShots([gone]);
    onChange(shots.filter(s => s.id !== id));
  };

  return (
    <div ref={ref}>
      <span className="mb-1.5 block text-[11.5px] font-bold text-[var(--ink-700)]">
        Captures d&apos;écran <span className="font-medium text-[var(--ink-500)]">· {shots.length} sur {SUPPORT_LIMITS.shotsMax}</span>
      </span>
      <div className="flex flex-wrap gap-2">
        {shots.map(s => (
          <div key={s.id} className="relative h-[58px] w-[86px] overflow-hidden rounded-lg border border-[var(--ink-200)] bg-[var(--ink-50)]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={s.url} alt="Capture d'écran" className="h-full w-full object-cover" />
            <button type="button" onClick={() => remove(s.id)} title="Retirer la capture"
              className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
              <X size={12} />
            </button>
          </div>
        ))}
        {pending && (
          <div className="grid h-[58px] w-[86px] place-items-center rounded-lg border border-[var(--ink-200)] bg-[var(--ink-50)] text-[var(--ink-400)]">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}
        {!full && !pending && (
          <button type="button" onClick={capture} title="Faire une capture de l'écran"
            className="grid h-[58px] w-[86px] place-items-center content-center gap-0.5 rounded-lg border border-dashed border-[var(--ink-300)] text-[11px] font-semibold text-[var(--ink-600)] hover:border-[var(--accent)] hover:text-[var(--accent-700)]">
            <Camera size={18} />Capturer
          </button>
        )}
      </div>
      <div className="mt-1 text-[11px] text-[var(--ink-500)]">
        {full
          ? 'Trois captures au plus. Retirez-en une pour en faire une autre.'
          : <>Ou collez une image (Ctrl+V), ou <button type="button" onClick={() => fileRef.current?.click()} className="font-semibold text-[var(--accent-700)] hover:underline">joignez un fichier</button>.</>}
      </div>
      <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden
        onChange={e => { void addFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
      {notice && <ShotNotice notice={notice} />}
    </div>
  );
}

function ShotNotice({ notice }: { notice: Exclude<Notice, null> }) {
  const tone = {
    added: 'border-green-200 bg-green-50 text-green-800',
    cancelled: 'border-amber-200 bg-amber-50 text-amber-800',
    fallback: 'border-[var(--accent-100)] bg-[var(--accent-50)] text-[var(--accent-700)]',
    failed: 'border-rose-200 bg-rose-50 text-rose-800',
    unsupported: 'border-amber-200 bg-amber-50 text-amber-800',
  }[notice];
  const Icon = { added: Check, cancelled: AlertTriangle, fallback: Info, failed: AlertTriangle, unsupported: AlertTriangle }[notice];
  const text = {
    added: 'Capture ajoutée. Vous pouvez en faire une autre après avoir changé de vue.',
    cancelled: 'La capture a été annulée. Vous pouvez réessayer, coller une image (Ctrl+V) ou joindre un fichier.',
    fallback: 'Votre navigateur ne sait pas capturer l\'écran : archifacil a photographié lui-même la page, vue 3D comprise. Le rendu peut différer un peu de ce que vous voyez.',
    failed: 'La capture n\'a pas pu être faite. Vous pouvez coller une image (Ctrl+V) ou joindre un fichier.',
    unsupported: 'Cette image n\'a pas été ajoutée : seules les images PNG, JPEG et WebP sont acceptées.',
  }[notice];
  return (
    <div role="status" className={`mt-2 flex gap-2 rounded-[10px] border px-3 py-2 text-xs leading-relaxed ${tone}`}>
      <Icon size={15} className="mt-0.5 flex-none" /><span>{text}</span>
    </div>
  );
}
